import React, { useState } from 'react';
import { Download, ArrowLeft, Printer, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';


// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const BusinessCardsPage = () => {
  const pdfUrl = `${API_HOST}/api/business-cards/download`;
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(pdfUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'MedCare_Business_Cards.pdf');
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const programs = [
    { name: 'CNA', full: 'Certified Medical Assistant (CMA) Training Program' },
    { name: 'PN Tech', full: 'Practical Nurse Tech' },
    { name: 'Phlebotomy', full: 'Phlebotomy Training' },
    { name: 'EMT', full: 'Emergency Medical Technician (EMT) Training' },
    { name: 'Homeland Security', full: 'Master Homeland Security Specialist' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #334155 100%)', 
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ArrowLeft size={20} />
          Back to Dashboard
        </Link>
        <h1 style={{ color: 'white', margin: 0, fontSize: '1.5rem' }}>MedCare Business Cards</h1>
        <button 
          onClick={handleDownload}
          style={{
            background: '#fbbf24',
            color: '#1e3a5f',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          <Download size={20} />
          Download PDF (9 Pages)
        </button>
      </div>
      
      {/* Instructions */}
      <div style={{ padding: '20px 40px', background: '#ecfdf5', borderBottom: '1px solid #a7f3d0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <Printer size={24} color="#16a34a" />
          <div>
            <strong style={{ color: '#166534' }}>Print Instructions:</strong>
            <span style={{ color: '#15803d', marginLeft: '8px' }}>
              Download the PDF → Open in Adobe Reader → Print on cardstock → Cut along edges (3.5" x 2" standard size)
            </span>
          </div>
        </div>
      </div>

      {/* Edge Blocker Help */}
      <div style={{ padding: '20px 40px', background: '#fef3c7', borderBottom: '1px solid #fcd34d' }}>
        <strong style={{ color: '#92400e' }}>If Microsoft Edge blocks the download:</strong>
        <ol style={{ margin: '8px 0 0 20px', color: '#a16207' }}>
          <li>Click the "..." menu in Edge's download bar</li>
          <li>Select "Keep" or "Keep anyway"</li>
          <li>Or copy this URL and paste in a new tab: 
            <code style={{ background: '#fef9c3', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>{pdfUrl}</code>
            <button 
              onClick={handleCopy}
              style={{ 
                marginLeft: '8px', 
                padding: '4px 8px', 
                background: copied ? '#22c55e' : '#1e3a5f', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
            </button>
          </li>
        </ol>
      </div>
      
      {/* Preview Card */}
      <div style={{ padding: '40px' }}>
        <h2 style={{ color: '#1e3a5f', marginBottom: '24px' }}>Card Preview:</h2>
        
        {/* Single Preview Card */}
        <div style={{ 
          display: 'flex',
          gap: '40px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {/* FRONT */}
          <div>
            <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '8px', fontWeight: 'bold' }}>FRONT</p>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              width: '350px',
              height: '200px',
              border: '1px solid #e2e8f0'
            }}>
              {/* Navy Header */}
              <div style={{ background: '#1e3a5f', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: '#d4a853' }}></div>
                {/* Logo */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '50%', 
                  width: '36px', 
                  height: '36px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  color: '#1e3a5f',
                  fontSize: '12px'
                }}>MC</div>
                <div>
                  <div style={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>MedCare Training Academy</div>
                  <div style={{ color: '#d4a853', fontSize: '0.6rem' }}>TN Dept of Labor Registered Apprenticeship Program</div>
                </div>
              </div>
              
              {/* Programs */}
              <div style={{ padding: '12px 16px' }}>
                <div style={{ color: '#1e3a5f', fontWeight: 'bold', fontSize: '0.75rem', marginBottom: '6px' }}>PROGRAMS OFFERED:</div>
                <div style={{ color: '#334155', fontSize: '0.7rem', lineHeight: '1.4' }}>
                  • CMA — Certified Medical Assistant<br/>
                  • PN Tech - Practical Nurse Tech<br/>
                  • Phlebotomy Technician<br/>
                  • EMT - Emergency Medical Technician<br/>
                  • Homeland Security Specialist
                </div>
              </div>
              
              {/* Footer */}
              <div style={{ background: '#f8fafc', padding: '10px 16px', borderTop: '2px solid #d4a853' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#1e3a5f', fontWeight: 'bold', fontSize: '0.8rem' }}>(901) 625-1201</div>
                  <div style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '0.65rem' }}>www.medcaretrainingacademy.com</div>
                  <div style={{ color: '#64748b', fontSize: '0.6rem' }}>3150 E. Shelby Dr, Memphis, TN 38118</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* BACK */}
          <div>
            <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '8px', fontWeight: 'bold' }}>BACK</p>
            <div style={{
              background: '#1e3a5f',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              width: '350px',
              height: '200px',
              position: 'relative',
              border: '6px solid #1e3a5f'
            }}>
              {/* Gold border */}
              <div style={{ 
                position: 'absolute', 
                inset: '6px', 
                border: '2px solid #d4a853', 
                borderRadius: '4px',
                pointerEvents: 'none'
              }}></div>
              
              {/* Top section with logo and name */}
              <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                {/* Logo */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '50%', 
                  width: '45px', 
                  height: '45px', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  color: '#1e3a5f',
                  fontSize: '14px',
                  marginBottom: '6px'
                }}>MC</div>
                
                <div style={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>MedCare Training Academy</div>
                <div style={{ color: '#d4a853', fontSize: '0.6rem', marginTop: '2px' }}>HEALTHCARE APPRENTICESHIP TRAINING</div>
              </div>
              
              {/* Contact section - at bottom */}
              <div style={{ 
                position: 'absolute', 
                bottom: '14px', 
                left: 0, 
                right: 0, 
                textAlign: 'center'
              }}>
                <div style={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>(901) 625-1201</div>
                <div style={{ color: '#94a3b8', fontSize: '0.55rem', marginTop: '3px' }}>medcare.nursing@yahoo.com</div>
                <div style={{ color: '#d4a853', fontWeight: 'bold', fontSize: '0.6rem', marginTop: '3px' }}>www.medcaretrainingacademy.com</div>
                <div style={{ color: '#94a3b8', fontSize: '0.5rem', marginTop: '3px' }}>3150 E. Shelby Dr, Memphis, TN 38118</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Download CTA */}
        <div style={{ 
          marginTop: '40px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #334155 100%)',
          padding: '32px',
          borderRadius: '16px'
        }}>
          <h3 style={{ color: 'white', marginBottom: '16px' }}>Ready to Print?</h3>
          <p style={{ color: '#cbd5e1', marginBottom: '24px' }}>
            The PDF includes 3 pages:<br/>
            • Page 1: Front design (10 cards)<br/>
            • Page 2: Back design for double-sided printing<br/>
            • Page 3: Alternative horizontal design
          </p>
          <button 
            onClick={handleDownload}
            style={{
              background: '#fbbf24',
              color: '#1e3a5f',
              padding: '16px 40px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <Download size={24} />
            Download Business Cards PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardsPage;
